// What the import dialog works out on its own, before the service sees the file — T43
// (6 Sep 2026). Pulled out of the screen because it is the part with rules in it, and this
// app's suite is the pure logic.
import type {
  ClientImportPreview,
  ColumnReading,
  ClientImportRowFields,
  ClientReadyRow,
  ImportOptionsResponse,
  ImportPreview,
  ImportReading,
  ImportRowFields,
  ReadyRow,
  RowEdit,
  StageReading,
} from '@vera/bff';
import { findByName } from '@vera/db/match-key';
import { stageLabel as placeLabel } from './pipeline.ts';
import { NO_COMPANY, type CompanyRoles, type RoleOption } from './roles.ts';

/**
 * The client and the role a file's NAME says, for the dialog's defaults.
 *
 * Mona's lists are one Google Sheets spreadsheet with a sheet per client, and Sheets names a
 * CSV export `<spreadsheet> - <sheet>.csv` — "VERA - Projects Pipeline_2026 - Pit.com — GTM
 * Director DACH (31).csv" was the first real one, the "(31)" being the browser's download
 * counter. The last " - " segment is the sheet; a dash inside it splits the client from the
 * role. A file with no " - " in its name — `longlist.csv` — says nothing, and nothing is guessed.
 */
export function defaultsFromFileName(name: string): { client: string | null; role: string | null } {
  const base = name
    .replace(/\.[A-Za-z0-9]+$/, '')
    .replace(/\s*\(\d+\)\s*$/, '')
    .trim();
  const segments = base.split(' - ');
  if (segments.length < 2) return { client: null, role: null };
  const sheet = segments[segments.length - 1]!.trim();
  const dash = sheet.split(/\s+[—–]\s+/);
  if (dash.length >= 2) {
    return { client: dash[0]!.trim() || null, role: dash.slice(1).join(' ').trim() || null };
  }
  return { client: sheet || null, role: null };
}

/** The dialog's two pickers: a company's id and a role's id, `''` for none. */
export interface ImportPick {
  client: string;
  role: string;
}

/**
 * T365c: the companies the Edit form's client picker offers. The roles on no company are not a
 * client the server could be told (their key is not an id): the picker's "None" is that choice.
 */
export function editableCompanies(groups: readonly CompanyRoles[]): CompanyRoles[] {
  return groups.filter((group) => group.key !== NO_COMPANY);
}

/**
 * The dialog's client and role — T113 (24 Sep 2026). PICKED from the brain's own lists, never
 * typed: a typed name that missed the real client by more than a typo was offered as a second
 * client and a second role beside it, which Import would have made. Natanel: "pick from a list of the clients and then after picking a
 * client, … pick from the list of the roles that client is offering".
 *
 * A file's name still fills them, as a pick: the company it names, matched as the import matches
 * names, then the role there whose title it names. A choice by hand (non-null in `chosen`) wins.
 * A name the file gave that matched nothing comes back in `missed` for the dialog to say so — it
 * is never made. With no company picked, the roles offered are those on no company, which is
 * where the import places a row naming no client.
 */
export function importPick(
  groups: readonly CompanyRoles[],
  named: { client: string | null; role: string | null },
  chosen: { client: string | null; role: string | null },
): {
  pick: ImportPick;
  companies: readonly CompanyRoles[];
  roles: readonly RoleOption[];
  missed: { client: string | null; role: string | null };
} {
  const companies = groups.filter((group) => group.key !== NO_COMPANY);
  const fromName = named.client ? (findByName(named.client, companies)?.item.key ?? null) : null;
  const client =
    chosen.client !== null
      ? companies.some((group) => group.key === chosen.client)
        ? chosen.client
        : ''
      : (fromName ?? '');
  const roles = groups.find((group) => group.key === (client || NO_COMPANY))?.roles ?? [];
  const titled = roles.map((role) => ({ id: role.id, name: role.title }));
  const roleFromName = named.role ? (findByName(named.role, titled)?.item.id ?? null) : null;
  const role =
    chosen.role !== null
      ? roles.some((option) => option.id === chosen.role)
        ? chosen.role
        : ''
      : (roleFromName ?? '');
  return {
    pick: { client, role },
    companies,
    roles,
    missed: {
      client: chosen.client === null && named.client && !fromName ? named.client : null,
      role: chosen.role === null && named.role && !roleFromName ? named.role : null,
    },
  };
}

/** The template as CSV text, from what the service says it reads. */
export function templateCsv(options: Pick<ImportOptionsResponse, 'headers' | 'example'>): string {
  const quote = (value: string) =>
    /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return `${options.headers.join(',')}\n${options.example.map(quote).join(',')}\n`;
}

/** Is this a workbook? Read as bytes and sent as base64; every sheet is one client. */
export const isWorkbook = (name: string): boolean => /\.xlsx$/i.test(name);

/** Bytes to base64, in chunks: `String.fromCharCode(...)` over two megabytes overflows the stack. */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let at = 0; at < bytes.length; at += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  }
  return btoa(binary);
}

/** The key the dialog's "create as new instead" switch is filed under — the typed name, folded. */
export const switchKey = (name: string): string => name.trim().toLowerCase();

/** A row's own key for the "new person" switch: the sheet and the row. */
export const rowKey = (ready: Pick<ReadyRow, 'sheet' | 'row'>): string =>
  `${ready.sheet}\u0000${ready.row}`;

/**
 * A confirmed PEOPLE row, as the service wants it: the preview's facts plus the two switches.
 *
 * It lives here rather than in the dialog because T83 (22 Sep 2026) gave the import a second door
 * — the chat, where kadmon previews the attached file and the card's Import files these same rows
 * (importChat.ts). One mapper, so the two doors cannot drift.
 */
export function personImportRow(
  ready: ReadyRow,
  newClients: ReadonlySet<string>,
  newRoles: ReadonlySet<string>,
  newPeople: ReadonlySet<string> = new Set(),
): ImportRowFields {
  const asNew = newPeople.has(switchKey(rowKey(ready)));
  const newClient = ready.client !== null && newClients.has(switchKey(ready.client.name));
  const newRole = ready.role !== null && newRoles.has(switchKey(ready.role.name));
  return {
    sheet: ready.sheet,
    row: ready.row,
    candidateId: asNew ? null : ready.candidateId,
    newPerson: asNew,
    // A new person has no number on file to keep: the file's own goes back on the entry.
    patch: asNew && ready.sheetNumber ? { ...ready.patch, e164: ready.sheetNumber } : ready.patch,
    client: ready.client?.name ?? null,
    role: ready.role?.name ?? null,
    // What the preview settled on goes by id — picked (T113), matched or pointed at (T113a) — so
    // the import files exactly what was shown. A name switched to new goes without one, and a
    // role goes without one when its client is being made new: that client has no roles yet.
    clientId: newClient ? null : (ready.client?.match?.id ?? null),
    searchId: newClient || newRole ? null : (ready.role?.match?.id ?? null),
    roleLocation: ready.roleLocation,
    // T155: everything the row said of the client contact, not only the name and the number.
    contact: ready.contact
      ? {
          name: ready.contact.name,
          e164: ready.contact.e164,
          email: ready.contact.email,
          title: ready.contact.title,
          linkedin: ready.contact.linkedin,
          notes: ready.contact.notes,
        }
      : null,
    stage: ready.stage,
    stageGiven: ready.stageGiven,
    note: ready.note,
    // T155: the row's columns no field holds, kept as a second note on the person.
    details: ready.details,
    // T365b: the role's own stage the person lands in, or the one this import adds for the word;
    // a row filed to a role that is new has no stages of its own yet, so only a stage being added.
    stepLabel:
      (newClient || newRole ? null : (ready.step?.label ?? null)) ?? ready.newStepLabel ?? null,
    addStep: ready.newStepLabel != null,
    newClient,
    newRole,
  };
}

/**
 * T133: is the row switched to a new person, client or role on the dialog? Then it lands where the
 * preview did not look, and nobody already on the role it looked at is moved or taken off.
 */
export function switchedToNew(
  ready: ReadyRow,
  newClients: ReadonlySet<string>,
  newRoles: ReadonlySet<string>,
  newPeople: ReadonlySet<string>,
): boolean {
  return (
    newPeople.has(switchKey(rowKey(ready))) ||
    (ready.client !== null && newClients.has(switchKey(ready.client.name))) ||
    (ready.role !== null && newRoles.has(switchKey(ready.role.name)))
  );
}

/**
 * T133: what the preview's Stage column says for a row. For someone already on the role, the
 * move the import makes (to Rejected for a "rejected" since T203), or where they stay; for anyone
 * else the stage they are placed at, or "not placed" for a row with no role.
 */
export function stageLabel(ready: ReadyRow, switchedNew: boolean): string {
  if (!ready.role) return '-';
  const change = switchedNew ? null : ready.stageChange;
  if (change) {
    return `${stageName(change.from, change.fromStep)} → ${stageName(change.to, change.toStep)}`;
  }
  const onRole = switchedNew ? null : ready.onRole;
  if (onRole) return `already ${stageName(onRole, ready.step?.label)}`;
  return ready.stage
    ? stageName(ready.stage, ready.step?.label ?? ready.newStepLabel)
    : 'not placed';
}

/** T365b: a stage as the brain names it — "Interviewed · Assignment" inside a role's own stage. */
const stageName = (stage: NonNullable<ReadyRow['stage']>, step?: string | null): string =>
  placeLabel(stage, step ? { label: step } : null);

/**
 * T365: what the preview's Phone column says for a row — the file's number, else the number the
 * brain already holds for the person it matched ("(on file)": a blank cell never clears it), else
 * nothing, and the screen says the row has none. A row switched to "create as new" is a new
 * person, so the number on file is not theirs.
 */
export function phoneLabel(
  ready: ReadyRow,
  switchedNewPerson: boolean,
): { text: string; onFile: boolean } | null {
  if (ready.e164) return { text: ready.e164, onFile: false };
  if (ready.numberOnFile && !switchedNewPerson) {
    return { text: `${ready.numberOnFile} (on file)`, onFile: true };
  }
  return null;
}

/**
 * A confirmed COMPANY row: the preview's facts plus the one switch. Here for the same reason. The
 * company and the contact the preview settled on go by id (T113a), unless the company is switched
 * to new — then it is made, and so is its contact.
 */
export function companyImportRow(
  ready: ClientReadyRow,
  newCompanies: ReadonlySet<string>,
): ClientImportRowFields {
  const newCompany = ready.match !== null && newCompanies.has(switchKey(ready.name));
  return {
    sheet: ready.sheet,
    row: ready.row,
    name: ready.name,
    newCompany,
    clientId: newCompany ? null : (ready.match?.id ?? null),
    fields: ready.fields,
    stageId: ready.stage?.id ?? null,
    reachingOutStaffId: ready.reachingOut?.id ?? null,
    // T155: every person the file brings the company, each with who they already are there —
    // none of them, when the company is switched to new.
    contacts: ready.contacts.map((person) => ({
      contact: person.contact,
      contactId: newCompany ? null : (person.match?.id ?? null),
    })),
    note: ready.note,
  };
}

/**
 * T155: the file's rows a company was read from — "2-4, 9", a run of rows as one, and a row on
 * another sheet named with it.
 */
export function rowsOf(
  ready: Pick<ClientImportPreview['ready'][number], 'sheet' | 'row' | 'rows'>,
): string {
  const parts: string[] = [];
  let run: { sheet: string; from: number; to: number } | null = null;
  const close = () => {
    if (!run) return;
    const span = run.from === run.to ? String(run.from) : `${run.from}-${run.to}`;
    parts.push(run.sheet === ready.sheet ? span : `${span} on ${run.sheet}`);
  };
  for (const at of ready.rows ?? [{ sheet: ready.sheet, row: ready.row }]) {
    if (run && run.sheet === at.sheet && at.row === run.to + 1) run.to = at.row;
    else {
      close();
      run = { sheet: at.sheet, from: at.row, to: at.row };
    }
  }
  close();
  return parts.join(', ');
}

// --- T113a: a name the preview called new, pointed at one already in the brain -----------------
//
// Lorenz, 25 Sep 2026: an import offered "new" clients and roles that were already in the brain,
// spelled another way. Anything the preview calls new is now offered a dropdown of what the brain
// has; a pick re-runs the preview with it, and the rows then land on the one that exists. What is
// left as new is made when the recruiter imports, and nothing else is.

/** A name the preview can point elsewhere, and where it points now: an id, or '' to make it new. */
export interface Pointable {
  name: string;
  value: string;
}

export interface PointableRole extends Pointable {
  clientId: string | null;
  /** The client as the rows named it; null for a role on no client. */
  client: string | null;
  /** Its client is new too: none of its roles exist to point at until the client is pointed. */
  clientIsNew: boolean;
}

export interface PointableContact extends Pointable {
  clientId: string;
  /** The company, as the brain names it. */
  company: string;
}

/** The people preview's pointings: clients by the name the file gave, roles by client and title. */
export interface PeopleLinks {
  clients: Readonly<Record<string, { name: string; clientId: string }>>;
  roles: Readonly<Record<string, { clientId: string | null; title: string; searchId: string }>>;
}

export const NO_PEOPLE_LINKS: PeopleLinks = { clients: {}, roles: {} };

/** A role's key among the pointings: its client and its title, as the service pairs them. */
export const roleLinkId = (clientId: string | null, title: string): string =>
  `${clientId ?? ''}\u0000${switchKey(title)}`;

/** The pointings with one client pointed — or, given '', made new again. */
export function pointClient(links: PeopleLinks, name: string, clientId: string): PeopleLinks {
  const clients = { ...links.clients };
  if (clientId === '') delete clients[switchKey(name)];
  else clients[switchKey(name)] = { name, clientId };
  return { ...links, clients };
}

/** The pointings with one role pointed — or, given '', made new again. */
export function pointRole(
  links: PeopleLinks,
  clientId: string | null,
  title: string,
  searchId: string,
): PeopleLinks {
  const roles = { ...links.roles };
  const key = roleLinkId(clientId, title);
  if (searchId === '') delete roles[key];
  else roles[key] = { clientId, title, searchId };
  return { ...links, roles };
}

/** The pointings as the preview's body carries them. */
export const peopleLinksBody = (links: PeopleLinks) => ({
  clients: Object.values(links.clients),
  roles: Object.values(links.roles),
});

/**
 * What a people preview can point at one already in the brain: every client and role it calls
 * new, and the ones already pointed, so a pointing can be changed or undone.
 */
export function pointableNames(preview: ImportPreview): {
  clients: Pointable[];
  roles: PointableRole[];
} {
  const clients = new Map<string, Pointable>();
  const roles = new Map<string, PointableRole>();
  for (const name of preview.summary.newClients) clients.set(switchKey(name), { name, value: '' });
  for (const role of preview.summary.newRoles) {
    roles.set(roleLinkId(role.clientId, role.title), {
      name: role.title,
      value: '',
      clientId: role.clientId,
      client: role.client,
      clientIsNew: role.client !== null && role.clientId === null,
    });
  }
  for (const row of preview.ready) {
    if (row.client?.linked && row.client.match) {
      clients.set(switchKey(row.client.name), {
        name: row.client.name,
        value: row.client.match.id,
      });
    }
    if (row.role?.linked && row.role.match) {
      const clientId = row.client?.match?.id ?? null;
      roles.set(roleLinkId(clientId, row.role.name), {
        name: row.role.name,
        value: row.role.match.id,
        clientId,
        client: row.client?.match?.name ?? null,
        clientIsNew: false,
      });
    }
  }
  return { clients: [...clients.values()], roles: [...roles.values()] };
}

/** The company preview's pointings: companies by the name the file gave, contacts by company. */
export interface CompanyLinks {
  companies: Readonly<Record<string, { name: string; clientId: string }>>;
  contacts: Readonly<Record<string, { clientId: string; name: string; contactId: string }>>;
}

export const NO_COMPANY_LINKS: CompanyLinks = { companies: {}, contacts: {} };

const contactLinkId = (clientId: string, name: string): string =>
  `${clientId}\u0000${switchKey(name)}`;

/** The pointings with one company pointed — or, given '', made new again. */
export function pointCompany(links: CompanyLinks, name: string, clientId: string): CompanyLinks {
  const companies = { ...links.companies };
  if (clientId === '') delete companies[switchKey(name)];
  else companies[switchKey(name)] = { name, clientId };
  return { ...links, companies };
}

/** The pointings with one contact pointed — or, given '', made new again. */
export function pointContact(
  links: CompanyLinks,
  clientId: string,
  name: string,
  contactId: string,
): CompanyLinks {
  const contacts = { ...links.contacts };
  const key = contactLinkId(clientId, name);
  if (contactId === '') delete contacts[key];
  else contacts[key] = { clientId, name, contactId };
  return { ...links, contacts };
}

/** The pointings as the company preview's body carries them. */
export const companyLinksBody = (links: CompanyLinks) => ({
  companies: Object.values(links.companies),
  contacts: Object.values(links.contacts),
});

/**
 * What a company preview can point at one already in the brain: every company it would make, and
 * every contact it would make at a company already here that has contacts of its own — with the
 * ones already pointed, so a pointing can be changed or undone.
 */
export function pointableCompanies(preview: ClientImportPreview): {
  companies: Pointable[];
  contacts: PointableContact[];
} {
  const companies = new Map<string, Pointable>();
  const contacts = new Map<string, PointableContact>();
  for (const row of preview.ready) {
    if (row.match === null) {
      companies.set(switchKey(row.name), { name: row.name, value: '' });
    } else if (row.match.linked) {
      companies.set(switchKey(row.name), { name: row.name, value: row.match.id });
    }
    if (row.match === null || (preview.contactsAt[row.match.id]?.length ?? 0) === 0) continue;
    // T155: every person the file brings a company already here, each pointable on its own.
    for (const person of row.contacts) {
      if (person.match !== null && person.match.by !== 'linked') continue;
      contacts.set(contactLinkId(row.match.id, person.contact.fullName), {
        name: person.contact.fullName,
        value: person.match?.id ?? '',
        clientId: row.match.id,
        company: row.match.name,
      });
    }
  }
  return { companies: [...companies.values()], contacts: [...contacts.values()] };
}

// T365a (15 Oct 2026): how the file was read, edited in the dialog. An edit is the recruiter's own
// word (`by: 'you'`): the next preview keeps it and reads the rest. The service holds the reading;
// these only change the copy the dialog sends back.

/** One header-row layout of the file: the sheets that share it, and their columns once. */
export interface ReadingLayout {
  key: string;
  sheets: string[];
  columns: ColumnReading[];
}

const layoutKeyOf = (columns: readonly ColumnReading[]): string =>
  columns.map((c) => `${c.index}:${switchKey(c.header)}`).join('\u0001');

/** The sheets' columns grouped by layout — sheets with the same headers are shown, and edited, once. */
export function readingLayouts(reading: ImportReading): ReadingLayout[] {
  const layouts = new Map<string, ReadingLayout>();
  for (const sheet of reading.sheets) {
    const key = layoutKeyOf(sheet.columns);
    const found = layouts.get(key);
    if (found) found.sheets.push(sheet.name);
    else layouts.set(key, { key, sheets: [sheet.name], columns: sheet.columns });
  }
  return [...layouts.values()];
}

/** A column's place as one select value: `field:<key>`, or `question:<key>` for a column of answers. */
export const columnChoice = (column: Pick<ColumnReading, 'field' | 'question'>): string =>
  column.field === 'answers' && column.question
    ? `question:${column.question}`
    : `field:${column.field}`;

/** The reading with one column of one layout set to a select value, in every sheet of that layout. */
export function withColumnEdit(
  reading: ImportReading,
  layoutKey: string,
  index: number,
  choice: string,
): ImportReading {
  const [kind, key] = [choice.slice(0, choice.indexOf(':')), choice.slice(choice.indexOf(':') + 1)];
  const edit = (column: ColumnReading): ColumnReading =>
    kind === 'question'
      ? { ...column, field: 'answers', question: key, by: 'you' }
      : { ...column, field: key as ColumnReading['field'], question: null, by: 'you' };
  return {
    ...reading,
    sheets: reading.sheets.map((sheet) =>
      layoutKeyOf(sheet.columns) === layoutKey
        ? { ...sheet, columns: sheet.columns.map((c) => (c.index === index ? edit(c) : c)) }
        : sheet,
    ),
  };
}

/** T365b: a stage word's select value for "add as a stage of this role, counting as <stage>". */
export const ADD_PREFIX = 'add:';

/** The stage a stage word's select holds: a stage, or '' for "says nothing". */
export const stageChoice = (reading: Pick<StageReading, 'stage' | 'addStep'>): string =>
  reading.addStep && reading.stage ? `${ADD_PREFIX}${reading.stage}` : (reading.stage ?? '');

/**
 * The reading with one stage word set to a stage ('' = says nothing). A new step is the
 * recruiter's now; choosing `add:<stage>` keeps it new and asks the import to add it to the role.
 */
export function withStageEdit(reading: ImportReading, word: string, choice: string): ImportReading {
  return {
    ...reading,
    stages: reading.stages.map((s) => {
      if (switchKey(s.word) !== switchKey(word)) return s;
      if (choice.startsWith(ADD_PREFIX)) {
        const stage = choice.slice(ADD_PREFIX.length) as NonNullable<StageReading['stage']>;
        return { ...s, stage, newStep: true, addStep: true, by: 'you' };
      }
      const plain: StageReading = {
        ...s,
        stage: choice === '' ? null : (choice as NonNullable<StageReading['stage']>),
        newStep: false,
        by: 'you',
      };
      delete plain.addStep;
      return plain;
    }),
  };
}

/**
 * T365c: what the Edit form changes on a row, folded into the dialog's edits (by `rowKey`). The
 * server applies them before it reads the row, so each re-preview sends them all. `fields` are
 * merged over earlier ones; `place` and `stage` replace, and `null` clears; `leftOut` false puts a
 * row back. A row left with nothing to say is dropped.
 */
export interface RowEditPatch {
  fields?: RowEdit['fields'];
  place?: RowEdit['place'] | null;
  stage?: RowEdit['stage'] | null;
  leftOut?: boolean;
}

export function withRowEdit(
  edits: ReadonlyMap<string, RowEdit>,
  at: { sheet: string; row: number },
  patch: RowEditPatch,
): Map<string, RowEdit> {
  const key = rowKey(at);
  const before = edits.get(key);
  const next: RowEdit = { sheet: at.sheet, row: at.row };
  const fields = { ...before?.fields, ...patch.fields };
  if (Object.keys(fields).length > 0) next.fields = fields;
  const place = patch.place === undefined ? before?.place : patch.place;
  if (place) next.place = place;
  const stage = patch.stage === undefined ? before?.stage : patch.stage;
  if (stage) next.stage = stage;
  if (patch.leftOut === undefined ? before?.leftOut : patch.leftOut) next.leftOut = true;
  const out = new Map(edits);
  if (next.fields || next.place || next.stage || next.leftOut) out.set(key, next);
  else out.delete(key);
  return out;
}

/** The fields of the form that differ from what the preview showed (a missing one is blank). */
export function changedFields(
  shown: NonNullable<RowEdit['fields']>,
  form: Readonly<Record<string, string>>,
): NonNullable<RowEdit['fields']> {
  const changed: Record<string, string> = {};
  for (const [field, value] of Object.entries(form)) {
    if (value !== ((shown as Record<string, string | undefined>)[field] ?? '')) {
      changed[field] = value;
    }
  }
  return changed as NonNullable<RowEdit['fields']>;
}
